import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBulkUpload } from "@/contexts/uploads/BulkUploadContext";
import {
  getMethodsForGroup,
  getUploadMethodGroup,
  UPLOAD_METHOD_GROUPS,
  UPLOAD_METHODS,
} from "@/hooks/uploads/uploadMethodsConfig";
import UploadMethodExplainer from "./UploadMethodExplainer";
import { useAudioRecording } from "@/hooks/uploads/useAudioRecording";
import { useUploadFormState } from "@/hooks/uploads/useUploadFormState";
import { cn } from "@/lib/utils";
import type {
  UploadMethod,
  UploadMethodConfig,
  UploadMethodGroupId,
  UploadNewSectionProps,
} from "@/types/knowledge/knowledge-acquisition";
import {
  UploadAudio,
  UploadBulk,
  UploadCloudStorage,
  UploadDeepResearch,
  UploadDocument,
  UploadImage,
  UploadNotes,
  UploadPdf,
  UploadQuickResearch,
  UploadUrl,
  UploadVideo,
  UploadWebScraping,
  UploadWebScrapingBrightData,
  UploadWebsite,
  UploadWebsiteBrightData,
  UploadYoutube,
} from "./upload";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const UploadNewSection = ({
  onUploadSuccess,
  availableAgents = [],
  initialMethod = "pdf",
  defaultAgentId = "none",
  initialTitle,
}: UploadNewSectionProps) => {
  const { setAgent, setAvailableAgents } = useBulkUpload();
  const formState = useUploadFormState(defaultAgentId, initialMethod);
  const {
    state,
    setActiveMethod,
    setPdfUrl,
    setPdfFile,
    setYoutubeUrl,
    setWebsiteUrl,
    setScrapeUrl,
    setAudioFile,
    setDocumentFile,
    setImageFiles,
    setVideoFile,
    setVideoUrl,
    setResearchQuery,
    setResearchTitle,
    setSelectedAgentId,
  } = formState;
  const [mobileGroupId, setMobileGroupId] = useState<UploadMethodGroupId>(
    getUploadMethodGroup(initialMethod)?.id ?? "files-cloud",
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  const { isRecording, isTranscribing, startRecording, stopRecording } = useAudioRecording(
    (transcription) => {
      setResearchQuery(transcription);
      setTimeout(autoResizeTextarea, 0);
    },
  );

  useEffect(() => {
    setAvailableAgents(availableAgents);
  }, [availableAgents, setAvailableAgents]);

  useEffect(() => {
    if (state.activeMethod !== "bulk") return;

    const selectedAgent = availableAgents.find((agent) => agent.id === state.selectedAgentId);
    setAgent(
      selectedAgent
        ? { id: selectedAgent.id, name: selectedAgent.name }
        : { id: undefined, name: undefined },
    );
  }, [availableAgents, setAgent, state.activeMethod, state.selectedAgentId]);

  useEffect(() => {
    const activeGroup = getUploadMethodGroup(state.activeMethod);
    if (activeGroup) setMobileGroupId(activeGroup.id);
  }, [state.activeMethod]);

  const handleAgentSelect = useCallback(
    (agentId: string) => {
      setSelectedAgentId(agentId);
    },
    [setSelectedAgentId],
  );

  const handleMethodChange = useCallback(
    (methodId: UploadMethod) => {
      if (methodId === "custom-integration") {
        window.open("https://calendly.com/sam-mckay/help-genie-discovery-call", "_blank");
        return;
      }
      setActiveMethod(methodId);
    },
    [setActiveMethod],
  );

  const uploadMethodsMap = {
    bulk: (
      <UploadBulk
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    pdf: (
      <UploadPdf
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        pdfFile={state.pdfFile}
        onPdfFileChange={setPdfFile}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    url: (
      <UploadUrl
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        pdfUrl={state.pdfUrl}
        onPdfUrlChange={setPdfUrl}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    youtube: (
      <UploadYoutube
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        youtubeUrl={state.youtubeUrl}
        onYoutubeUrlChange={setYoutubeUrl}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    website: (
      <UploadWebsite
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        websiteUrl={state.websiteUrl}
        onWebsiteUrlChange={setWebsiteUrl}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    "website-brightdata": (
      <UploadWebsiteBrightData
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        websiteUrl={state.websiteUrl}
        onWebsiteUrlChange={setWebsiteUrl}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    audio: (
      <UploadAudio
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        audioFile={state.audioFile}
        onAudioFileChange={setAudioFile}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={undefined}
      />
    ),
    document: (
      <UploadDocument
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        documentFile={state.documentFile}
        onDocumentFileChange={setDocumentFile}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={undefined}
      />
    ),
    image: (
      <UploadImage
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        imageFiles={state.imageFiles}
        onImageFilesChange={setImageFiles}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={undefined}
      />
    ),
    video: (
      <UploadVideo
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        videoFile={state.videoFile}
        onVideoFileChange={setVideoFile}
        videoUrl={state.videoUrl}
        onVideoUrlChange={setVideoUrl}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={undefined}
      />
    ),
    notes: (
      <UploadNotes
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        researchQuery={state.researchQuery}
        researchTitle={state.researchTitle}
        onResearchQueryChange={setResearchQuery}
        onResearchTitleChange={setResearchTitle}
        onUploadSuccess={onUploadSuccess}
        initialTitle={initialTitle}
      />
    ),
    "web-scraping": (
      <UploadWebScraping
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        scrapeUrl={state.scrapeUrl}
        onScrapeUrlChange={setScrapeUrl}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    "website-crawl": (
      <UploadWebScrapingBrightData
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        scrapeUrl={state.scrapeUrl}
        onScrapeUrlChange={setScrapeUrl}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    "quick-research": (
      <UploadQuickResearch
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        researchQuery={state.researchQuery}
        researchTitle={state.researchTitle}
        textareaRef={textareaRef}
        isRecording={isRecording}
        isTranscribing={isTranscribing}
        onResearchQueryChange={setResearchQuery}
        onResearchTitleChange={setResearchTitle}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    "deep-research": (
      <UploadDeepResearch
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        researchQuery={state.researchQuery}
        researchTitle={state.researchTitle}
        textareaRef={textareaRef}
        isRecording={isRecording}
        isTranscribing={isTranscribing}
        onResearchQueryChange={setResearchQuery}
        onResearchTitleChange={setResearchTitle}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
        onUploadSuccess={onUploadSuccess}
      />
    ),
    "cloud-storage": (
      <UploadCloudStorage
        availableAgents={availableAgents}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        onUploadSuccess={onUploadSuccess}
      />
    ),
  };

  const mobileMethods = useMemo(() => {
    const activeGroup = UPLOAD_METHOD_GROUPS.find((group) => group.id === mobileGroupId);
    return activeGroup ? getMethodsForGroup(activeGroup) : [];
  }, [mobileGroupId]);

  const renderMethodButton = (method: UploadMethodConfig) => {
    const isActive = state.activeMethod === method.id;

    return (
      <button
        key={method.id}
        type="button"
        aria-pressed={isActive}
        onClick={() => !method.comingSoon && handleMethodChange(method.id)}
        disabled={method.comingSoon}
        className={cn(
          "group relative flex min-h-14 w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2",
          isActive
            ? "border-genie-primary bg-gradient-to-r from-genie-primary/5 to-genie-secondary/5 shadow-sm"
            : "border-gray-200 bg-white hover:border-genie-primary/40 hover:bg-gray-50",
          method.comingSoon &&
            "cursor-not-allowed opacity-60 hover:border-gray-200 hover:bg-white",
        )}
      >
        <GenieIcon
          name={method.genieIcon}
          size={22}
          color={isActive ? "#4E9CFF" : "#9CA3AF"}
          secondaryColor={isActive ? "#FF4DB8" : undefined}
          gradientEnabled={isActive}
          className="flex-shrink-0"
        />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block text-[13px] font-semibold leading-tight",
              isActive ? "text-genie-primary" : "text-gray-900",
            )}
          >
            {method.label}
          </span>
          <span className="mt-0.5 block text-[10px] leading-snug text-gray-500">
            {method.description}
          </span>
        </span>
        {method.comingSoon && (
          <span className="flex-shrink-0 rounded-full bg-genie-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-genie-primary">
            Soon
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(340px,380px)_1fr] lg:gap-5">
      <div className="self-start lg:sticky lg:top-6">
        <div className="mb-3 lg:hidden">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Choose a source
          </p>
          <div className="flex gap-2 overflow-x-auto pb-2" aria-label="Acquisition categories">
            {UPLOAD_METHOD_GROUPS.map((group) => (
              <button
                key={group.id}
                type="button"
                aria-pressed={mobileGroupId === group.id}
                onClick={() => setMobileGroupId(group.id)}
                className={cn(
                  "whitespace-nowrap rounded-full border px-3 py-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary",
                  mobileGroupId === group.id
                    ? "border-genie-primary bg-genie-primary/10 text-genie-primary"
                    : "border-gray-200 bg-white text-gray-600 hover:border-genie-primary/40",
                )}
              >
                {group.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
            {mobileMethods.map(renderMethodButton)}
          </div>
        </div>

        <div className="hidden space-y-5 lg:block">
          {UPLOAD_METHOD_GROUPS.map((group) => (
            <section key={group.id} aria-labelledby={`upload-group-${group.id}`}>
              <div className="mb-2">
                <h3
                  id={`upload-group-${group.id}`}
                  className="text-sm font-semibold text-gray-900"
                >
                  {group.label}
                </h3>
                <p className="text-[11px] text-gray-500">{group.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {getMethodsForGroup(group).map(renderMethodButton)}
              </div>
            </section>
          ))}
        </div>
      </div>

      <div className="self-start rounded-2xl border border-gray-200 bg-white p-4 shadow-lg sm:p-6">
        <UploadMethodExplainer
          method={state.activeMethod}
          methodLabel={
            UPLOAD_METHODS.find((method) => method.id === state.activeMethod)?.label ?? "this"
          }
        />
        {uploadMethodsMap[state.activeMethod as keyof typeof uploadMethodsMap] || null}
      </div>
    </div>
  );
};

export default UploadNewSection;
